import React from "react";
import Select from "react-select";
import Flatpickr from "react-flatpickr";

/**
 * Shared building blocks for the filter bar above list tables. Every filter
 * clears itself in place — there is no separate reset control — so dropping one
 * choice never discards the rest.
 */

// Colours come from theme variables so the selects follow light and dark
// mode instead of staying white with light text in the dark theme.
export const filterSelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: "var(--vz-input-bg-custom)",
    borderColor: state.isFocused
      ? "var(--vz-primary-border-subtle)"
      : "var(--vz-input-border-custom)",
    borderRadius: ".25rem",
    boxShadow: state.isFocused
      ? "0 0 0 0.25rem rgba(var(--vz-primary-rgb), 0.25)"
      : "none",
    "&:hover": { borderColor: "var(--vz-input-border-custom)" },
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: "var(--vz-body-color)",
  }),
  input: (provided: any) => ({ ...provided, color: "var(--vz-body-color)" }),
  placeholder: (provided: any) => ({
    ...provided,
    color: "var(--cims-ink-muted)",
  }),
  menu: (provided: any) => ({
    ...provided,
    backgroundColor: "var(--vz-secondary-bg)",
    border: "1px solid var(--vz-border-color)",
    borderRadius: ".25rem",
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "var(--vz-primary)"
      : state.isFocused
      ? "var(--vz-tertiary-bg)"
      : "transparent",
    color: state.isSelected ? "#fff" : "var(--vz-body-color)",
  }),
};

export const FilterLabel = ({
  htmlFor,
  children,
}: {
  htmlFor: string;
  children: React.ReactNode;
}) => (
  <label
    htmlFor={htmlFor}
    className="form-label text-ink-muted fw-medium fs-13 mb-1"
  >
    {children}
  </label>
);

const ClearButton = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    className="btn btn-link text-muted p-0 position-absolute top-50 end-0 translate-middle-y me-3 lh-1"
    onClick={onClick}
  >
    <i className="ri-close-circle-fill fs-16"></i>
  </button>
);

export const FilterSearchBox = ({
  id = "filter-search",
  label = "Search",
  placeholder = "Search...",
  value,
  onChange,
}: {
  id?: string;
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <>
    <FilterLabel htmlFor={id}>{label}</FilterLabel>
    <div className="search-box">
      <input
        id={id}
        type="text"
        className="form-control search"
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        value={value || ""}
      />
      <i className="ri-search-line search-icon"></i>
      {value ? (
        <ClearButton label="Clear search" onClick={() => onChange("")} />
      ) : null}
    </div>
  </>
);

export type DateRangeValue = {
  fromDate: Date | null;
  toDate: Date | null;
};

export const FilterDateRange = ({
  id = "filter-date-range",
  label = "Date Range",
  placeholder = "Select a date range",
  value,
  onChange,
}: {
  id?: string;
  label?: string;
  placeholder?: string;
  value: DateRangeValue | undefined;
  onChange: (value: DateRangeValue) => void;
}) => (
  <>
    <FilterLabel htmlFor={id}>{label}</FilterLabel>
    <div className="position-relative">
      {/* @ts-ignore */}
      <Flatpickr
        id={id}
        className="form-control"
        placeholder={placeholder}
        options={{ mode: "range", altFormat: "d M, Y", dateFormat: "d M, Y" }}
        onChange={(dates: Date[]) => {
          if (dates[0] && dates[1]) {
            onChange({ fromDate: dates[0], toDate: dates[1] });
          }
        }}
        value={[value?.fromDate || "", value?.toDate || ""]}
      />
      {value?.fromDate ? (
        <ClearButton
          label="Clear date range"
          onClick={() => onChange({ fromDate: null, toDate: null })}
        />
      ) : null}
    </div>
  </>
);

/**
 * A clearable dropdown holding the whole `{ value, label }` option, so the
 * choice survives a reload from the URL without a second lookup.
 */
export const FilterSelect = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  options,
  isLoading,
  formatOptionLabel,
}: {
  id: string;
  label?: string;
  placeholder: string;
  value: any;
  onChange: (value: any) => void;
  options: any[];
  isLoading?: boolean;
  /** Custom option content, e.g. a rating's colour dot before its name. */
  formatOptionLabel?: (option: any) => React.ReactNode;
}) => (
  <>
    {label ? <FilterLabel htmlFor={id}>{label}</FilterLabel> : null}
    <Select
      name={id}
      inputId={id}
      // Compare against empty rather than falsy so a `0` option (e.g. admin
      // level 0) still shows as selected.
      value={
        value?.value !== undefined && value?.value !== null && value?.value !== ""
          ? value
          : null
      }
      placeholder={placeholder}
      onChange={onChange}
      options={options}
      isLoading={isLoading}
      isClearable
      formatOptionLabel={formatOptionLabel}
      styles={filterSelectStyles}
    />
  </>
);

/**
 * The same dropdown for the pages whose filter state is a bare id rather than
 * an option object; it resolves the id against the loaded options and emits a
 * bare id back (empty string when cleared).
 */
export const FilterValueSelect = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  options,
  isLoading,
  formatOptionLabel,
}: {
  id: string;
  label?: string;
  placeholder: string;
  value: any;
  onChange: (value: any) => void;
  options: any[];
  isLoading?: boolean;
  formatOptionLabel?: (option: any) => React.ReactNode;
}) => (
  <FilterSelect
    id={id}
    label={label}
    placeholder={placeholder}
    options={options}
    isLoading={isLoading}
    formatOptionLabel={formatOptionLabel}
    value={
      options.find((option: any) => String(option.value) === String(value)) ||
      null
    }
    onChange={(option: any) => onChange(option?.value ?? "")}
  />
);
